// 股票详情抽屉（Web 简化版） - K线 + 现货交易 + 合约交易
import { useState } from 'react';
import { Drawer, Tabs, Space, InputNumber, Button, Statistic, Tag, Alert, App as AntApp, Radio, Select } from 'antd';
import {
  RiseOutlined,
  FallOutlined,
} from '@ant-design/icons';
import KLineChart from './KLineChart';
import { useStore } from '../store/account';
import { CONTRACT_LEVERAGES, CONTRACT_MAINTENANCE_MARGIN_RATIO } from '../../shared/constants';
import type { ContractPosition, StockBasic, StockQuote } from '../../shared/types';

interface Props {
  open: boolean;
  stock: StockBasic | null;
  quote: StockQuote | null;
  onClose: () => void;
}

export default function StockDetailDrawer({ open, stock, quote, onClose }: Props) {
  const {
    account,
    refreshAccount,
    buy,
    sell,
    openContract,
    closeContract,
    setContractTPSL,
    quotes,
  } = useStore();
  const { message } = AntApp.useApp();
  const [tab, setTab] = useState<'kline' | 'spot' | 'contract'>('kline');
  const [busy, setBusy] = useState(false);

  // 现价（实时）
  const liveQuote = stock ? quotes[stock.code] : null;
  const livePrice = liveQuote?.price ?? quote?.price ?? 0;

  // 现货表单
  const [spotSide, setSpotSide] = useState<'buy' | 'sell'>('buy');
  const [spotShares, setSpotShares] = useState<number>(100);

  // 合约表单
  const [contractSide, setContractSide] = useState<'long' | 'short'>('long');
  const [contractLeverage, setContractLeverage] = useState<1 | 5 | 10 | 20>(5);
  const [contractSize, setContractSize] = useState<number>(1);

  if (!stock) return null;

  const positions = (account?.contracts ?? []).filter((p: ContractPosition) => p.code === stock.code);

  const handleSpot = async () => {
    if (!account) return;
    setBusy(true);
    try {
      const fn = spotSide === 'buy' ? buy : sell;
      const r = await fn({ code: stock.code, shares: spotShares });
      if (r.ok) message.success(r.message);
      else message.warning(r.message);
      await refreshAccount();
    } finally {
      setBusy(false);
    }
  };

  const handleOpenContract = async () => {
    setBusy(true);
    try {
      const r = await openContract({
        code: stock.code,
        side: contractSide,
        leverage: contractLeverage,
        size: contractSize,
      });
      if (r.ok) message.success(r.message);
      else message.warning(r.message);
      await refreshAccount();
    } finally {
      setBusy(false);
    }
  };

  const handleCloseContract = async (id: string) => {
    setBusy(true);
    try {
      const r = await closeContract(id, livePrice);
      if (r.ok) message.success(r.message);
      else message.warning(r.message);
      await refreshAccount();
    } finally {
      setBusy(false);
    }
  };

  const marginRequired = livePrice * contractSize * (1 / contractLeverage);
  const available = account?.cash ?? 0;

  return (
    <Drawer
      title={`${stock.name} · ${stock.code}`}
      placement="right"
      width={720}
      open={open}
      onClose={onClose}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Statistic
          title="现价"
          value={livePrice}
          prefix="¥"
          valueStyle={{ color: liveQuote && liveQuote.changePercent >= 0 ? '#ff4d4f' : '#52c41a' }}
        />
        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as any)}
          items={[
            {
              key: 'kline',
              label: 'K 线',
              children: <KLineChart stock={stock} height={320} />,
            },
            {
              key: 'spot',
              label: '现货',
              children: (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Radio.Group
                    value={spotSide}
                    onChange={(e) => setSpotSide(e.target.value)}
                    buttonStyle="solid"
                  >
                    <Radio.Button value="buy">买入</Radio.Button>
                    <Radio.Button value="sell">卖出</Radio.Button>
                  </Radio.Group>
                  <div>
                    <div style={{ marginBottom: 4 }}>数量（股）</div>
                    <InputNumber
                      min={100}
                      step={100}
                      value={spotShares}
                      onChange={(v) => setSpotShares(Number(v) || 0)}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    预估金额：<b>¥{(livePrice * spotShares).toLocaleString()}</b>
                  </div>
                  <Button
                    type="primary"
                    danger={spotSide === 'sell'}
                    loading={busy}
                    disabled={!account}
                    onClick={handleSpot}
                  >
                    {spotSide === 'buy' ? '买入' : '卖出'}
                  </Button>
                </Space>
              ),
            },
            {
              key: 'contract',
              label: '合约',
              children: (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Radio.Group
                    value={contractSide}
                    onChange={(e) => setContractSide(e.target.value)}
                    buttonStyle="solid"
                  >
                    <Radio.Button value="long">
                      <RiseOutlined /> 做多
                    </Radio.Button>
                    <Radio.Button value="short">
                      <FallOutlined /> 做空
                    </Radio.Button>
                  </Radio.Group>
                  <div>
                    <div style={{ marginBottom: 4 }}>杠杆</div>
                    <Select
                      value={contractLeverage}
                      onChange={(v) => setContractLeverage(v)}
                      options={CONTRACT_LEVERAGES.map((l) => ({ value: l, label: `${l}x` }))}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <div style={{ marginBottom: 4 }}>数量（张）</div>
                    <InputNumber
                      min={1}
                      value={contractSize}
                      onChange={(v) => setContractSize(Number(v) || 1)}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <Alert
                    type="info"
                    message={`保证金 ¥${marginRequired.toLocaleString()}（杠杆 ${contractLeverage}x · 维持保证金率 ${(CONTRACT_MAINTENANCE_MARGIN_RATIO * 100).toFixed(0)}%）`}
                  />
                  <Button type="primary" loading={busy} disabled={marginRequired > available} onClick={handleOpenContract}>
                    开仓
                  </Button>

                  {positions.length > 0 && (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <div style={{ fontWeight: 600 }}>持仓</div>
                      {positions.map((p: ContractPosition) => (
                        <ContractPositionCard
                          key={p.id}
                          pos={p}
                          livePrice={livePrice}
                          onClose={() => handleCloseContract(p.id)}
                          onSetTPSL={(tp, sl) => setContractTPSL(p.id, tp, sl)}
                          busy={busy}
                        />
                      ))}
                    </Space>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Space>
    </Drawer>
  );
}

interface ContractPositionCardProps {
  pos: ContractPosition;
  livePrice: number;
  onClose: () => void;
  onSetTPSL: (tp?: number, sl?: number) => Promise<{ ok: boolean; message: string }>;
  busy: boolean;
}

function ContractPositionCard({ pos, livePrice, onClose, onSetTPSL, busy }: ContractPositionCardProps) {
  const { message } = AntApp.useApp();
  const [tp, setTp] = useState<number | null>(pos.takeProfit ?? null);
  const [sl, setSl] = useState<number | null>(pos.stopLoss ?? null);

  // 简化 PnL：单向杠杆 PnL = (现价 - 入场价) * 杠杆 * 张数
  const direction = pos.side === 'long' ? 1 : -1;
  const pnl = ((livePrice - pos.entryPrice) / pos.entryPrice) * pos.leverage * pos.size * direction;

  const handleSaveTPSL = async () => {
    const r = await onSetTPSL(tp ?? undefined, sl ?? undefined);
    if (r.ok) message.success('已更新止盈止损');
    else message.warning(r.message);
  };

  return (
    <div
      style={{
        background: '#1c2128',
        border: '1px solid #30363d',
        borderRadius: 4,
        padding: 8,
      }}
    >
      <Space style={{ marginBottom: 8 }}>
        <Tag color={pos.side === 'long' ? 'red' : 'green'}>
          {pos.side === 'long' ? '多' : '空'} {pos.leverage}x
        </Tag>
        <span style={{ color: pnl >= 0 ? '#ff4d4f' : '#52c41a', fontWeight: 600 }}>
          {pnl >= 0 ? '+' : ''}¥{pnl.toFixed(2)}
        </span>
      </Space>
      <div style={{ fontSize: 12, color: '#8b949e' }}>
        入场 ¥{pos.entryPrice.toLocaleString()} · 现价 ¥{livePrice.toLocaleString()} · {pos.size} 张
      </div>
      <Space style={{ marginTop: 8 }}>
        <InputNumber
          placeholder="止盈"
          value={tp ?? undefined}
          onChange={(v) => setTp(v === null ? null : Number(v))}
          style={{ width: 100 }}
        />
        <InputNumber
          placeholder="止损"
          value={sl ?? undefined}
          onChange={(v) => setSl(v === null ? null : Number(v))}
          style={{ width: 100 }}
        />
        <Button size="small" loading={busy} onClick={handleSaveTPSL}>
          保存
        </Button>
        <Button size="small" danger loading={busy} onClick={onClose}>
          平仓
        </Button>
      </Space>
    </div>
  );
}